import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, ArrowRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fadeUp, viewport } from "@/lib/motion";
import { TID } from "@/constants/site";
import AidaWidget from "@/components/AidaWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  "Explore an AI opportunity for your business",
  "Discuss an idea for an application",
  "Assess where AI could help your SME",
  "Explore a technology partnership",
  "Ask about something we have in development",
];

const Connect = ({ onAida }) => {
  const [form, setForm] = useState({ name: "", email: "", reason: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.reason || !form.message) {
      toast.error("Please fill in your name, email, reason and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll get back to you with a clear next step, not a sales pitch.");
      setForm({ name: "", email: "", reason: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="connect" className="relative overflow-hidden py-28 lg:py-36 border-t border-black/5">
      <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 h-[400px] w-[700px] rounded-full bg-[#43B0CE]/8 blur-[130px]" />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
          <span className="badge-pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[#43B0CE]" />
            Connect
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-4xl leading-[1.1]">
            Whatever brought you here, <span className="text-[#43B0CE]">we'd like to hear about it.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-lg font-light leading-relaxed text-[#3C5156]">
            There's no wrong reason to start a conversation — and no obligation once you do. We'll read your
            message, understand what you're trying to achieve, and get back to you with a clear next step.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {/* Tell Aida — embedded chat */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="flex flex-col rounded-3xl border border-[#43B0CE]/25 bg-gradient-to-b from-[#F4FAFB] to-[#FFFFFF] p-6 sm:p-8"
          >
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#43B0CE]/40 bg-[#43B0CE]/10 px-4 py-1.5">
              <Sparkles className="h-4 w-4 text-[#43B0CE]" />
              <span className="font-mono-ed text-[10px] tracking-[0.2em] uppercase text-[#43B0CE]">Ask Aida</span>
            </div>
            <h3 className="mt-5 font-display text-2xl font-bold text-[#0B2429]">Tell Aida what you need</h3>
            <p className="mb-6 mt-3 text-[15px] leading-relaxed text-[#3C5156]">
              Chat with Aida right here — or use the form. Either way, it reaches the team.
            </p>
            <div className="h-[520px]">
              <AidaWidget embedded section="connect" />
            </div>
          </motion.div>

          {/* Traditional form */}
          <motion.form
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            onSubmit={submit}
            className="rounded-3xl border border-black/10 bg-[#FFFFFF] p-8 lg:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name">
                <input data-testid={TID.connect.name} value={form.name} onChange={set("name")} placeholder="Your name"
                  className="w-full rounded-xl bg-[#EDF8F8] border border-black/10 px-4 py-3 text-[15px] text-[#0B2429] placeholder:text-[#94A7AC] focus:border-[#7FD0E6]/50 focus:outline-none transition-colors" />
              </Field>
              <Field label="Work email">
                <input data-testid={TID.connect.email} type="email" value={form.email} onChange={set("email")} placeholder="you@company.com"
                  className="w-full rounded-xl bg-[#EDF8F8] border border-black/10 px-4 py-3 text-[15px] text-[#0B2429] placeholder:text-[#94A7AC] focus:border-[#7FD0E6]/50 focus:outline-none transition-colors" />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-5 mt-5">
              <Field label="Reason for contact">
                <Select value={form.reason} onValueChange={set("reason")}>
                  <SelectTrigger data-testid={TID.connect.reason} className="w-full rounded-xl bg-[#EDF8F8] border-black/10 px-4 py-3 h-auto text-[15px] text-[#0B2429] focus:ring-0 focus:border-[#7FD0E6]/50">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-black/10 text-[#0B2429]">
                    {REASONS.map((r) => (
                      <SelectItem key={r} value={r} className="text-[13px] focus:bg-[#43B0CE]/20 focus:text-[#0B2429]">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Company (optional)">
                <input data-testid={TID.connect.company} value={form.company} onChange={set("company")} placeholder="Company name"
                  className="w-full rounded-xl bg-[#EDF8F8] border border-black/10 px-4 py-3 text-[15px] text-[#0B2429] placeholder:text-[#94A7AC] focus:border-[#7FD0E6]/50 focus:outline-none transition-colors" />
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Message">
                <textarea data-testid={TID.connect.message} value={form.message} onChange={set("message")} rows={5} placeholder="Tell us what you're trying to achieve…"
                  className="w-full rounded-xl bg-[#EDF8F8] border border-black/10 px-4 py-3 text-[15px] text-[#0B2429] placeholder:text-[#94A7AC] focus:border-[#7FD0E6]/50 focus:outline-none transition-colors resize-none" />
              </Field>
            </div>

            <button
              data-testid={TID.connect.submit}
              type="submit"
              disabled={loading}
              className="btn-primary mt-6 group px-7 py-3.5 text-sm disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {loading ? "Sending…" : "Get in touch"}
              {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-ed text-[10px] tracking-[0.2em] uppercase text-[#64787E]">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

export default Connect;
